import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyzerConfig, ContractType, Finding, Jurisdiction } from '../../types/contractAnalyzer';
import { storage } from '../../lib/storage';
import { extractContractText } from '../../utils/contractExtraction';
import FindingsView from '../../components/legal-hr/FindingsView';
import { runLocalRules } from '../../services/contractRules';
import { saveRecommendations } from '../../services/contractAnalyzerService';
import { toast } from 'react-hot-toast';
import FindingsFilters, { Filters } from '../../components/legal-hr/FindingsFilters';
import { analyzeWithLLM, analyzeWithLLMChunked } from '../../services/contractLLMService';
import { detectContractType } from '../../services/contractTypeDetector';

const jurisdictions = ['UK', 'USA', 'EU', 'Canada', 'Australia'] as const;

const ContractAnalyzer: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<Jurisdiction[]>(['UK']);
  const [primaryJurisdiction, setPrimaryJurisdiction] = useState<Jurisdiction>('UK');
  const [contractType, setContractType] = useState<ContractType>('NDA');
  const [file, setFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [exportingType, setExportingType] = useState<'markdown' | 'json' | 'csv' | null>(null);
  const [filters, setFilters] = useState<Filters>({ severity: 'all', jurisdiction: 'all', category: 'all' });
  const [, setTokenUsage] = useState<number>(0);
  // Optional analysis preferences (used by NEC3 and JCT rules for tailored recommendations)
  const [nec3FloatOwnership, setNec3FloatOwnership] = useState<'Contractor' | 'Employer' | 'Neutral'>('Neutral');
  const [nec3CEFloatTreatment, setNec3CEFloatTreatment] = useState<'IgnoreFloat' | 'UseFloat' | 'NotSpecified'>('NotSpecified');

  useEffect(() => {
    document.title = 'Contract Analyzer (Beta) — Explanatum';
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
  };

  const uploadExportArtifact = async (
    blob: Blob,
    fileName: string,
    docType: string,
    tags: string[],
    contextSummary?: string | null
  ) => {
    if (!user?.id) {
      throw new Error('Authentication required to export findings');
    }

    const extension = fileName.split('.').pop() || 'txt';
    const baseName = fileName.replace(new RegExp(`\\.${extension}$`, 'i'), '');
    const safeSlug = baseName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'contract-findings';

    const timestamp = new Date();
    const storagePath = `contract-analyzer/${user.id}/${timestamp.getTime()}-${safeSlug}.${extension}`;
    const storageFile = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });

    const upload = await storage.uploadFile({
      file: storageFile,
      path: storagePath,
      metadata: {
        sourceModule: 'contract-analyzer',
        docType,
        visibility: 'private',
        tags: Array.from(
          new Set(
            [
              `contract-type:${contractType}`,
              `primary-jurisdiction:${primaryJurisdiction}`,
              ...(file ? [`source-file:${file.name}`] : []),
              ...tags,
            ].filter(Boolean)
          )
        ),
        contractType,
        jurisdictions: selectedJurisdictions,
        primaryJurisdiction,
        contextSummary: contextSummary ?? null,
        generatedAt: timestamp.toISOString(),
      },
    });

    const signedUrl = await storage.getSignedUrl(upload.key, 600);
    return { signedUrl, key: upload.key };
  };

  const openSignedDownload = (signedUrl: string) => {
    const link = document.createElement('a');
    link.href = signedUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  const filteredFindings = findings.filter((f) => {
    const bySev = filters.severity === 'all' || f.severity === filters.severity;
    const byJur = filters.jurisdiction === 'all' || f.jurisdiction === filters.jurisdiction;
    const byCat = filters.category === 'all' || f.category === filters.category;
    const isAmendments =
      /schedule of amendments|amendments to jct/i.test(f.title) ||
      /schedule of amendments|amendments to jct/i.test(f.summary) ||
      /jct-schedule-amendments-impact/i.test(f.id);
    const byAmend = !filters.amendmentsOnly || isAmendments;
    return bySev && byJur && byCat && byAmend;
  });

  const exportFindings = async (format: 'json' | 'csv' | 'markdown') => {
    if (!filteredFindings.length) {
      toast.error('No findings to export');
      return;
    }

    try {
      setExportingType(format);

      if (format === 'json') {
        const payload = {
          generatedAt: new Date().toISOString(),
          contractType,
          jurisdictions: selectedJurisdictions,
          findings: filteredFindings,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
        const fileName = `contract-findings-${new Date().toISOString().slice(0, 10)}.json`;
        const { signedUrl } = await uploadExportArtifact(
          blob,
          fileName,
          'analysis-json',
          ['findings-export', 'format:json'],
          'Contract analyzer findings (JSON)'
        );
        openSignedDownload(signedUrl);
        toast.success('Findings exported to storage (JSON)');
        return;
      }

      if (format === 'csv') {
        const header = ['title', 'category', 'severity', 'jurisdiction', 'contract_type', 'confidence', 'summary', 'recommendation'];
        const escapeCsvValue = (value: string) => {
          const escaped = value.replace(/"/g, '""');
          return /[",\n]/.test(value) ? `"${escaped}"` : escaped;
        };
        const rows = filteredFindings.map((finding) => [
          escapeCsvValue(finding.title),
          escapeCsvValue(finding.category),
          escapeCsvValue(finding.severity),
          escapeCsvValue(finding.jurisdiction),
          escapeCsvValue(finding.contractType),
          escapeCsvValue((finding.confidence?.score ?? 0).toFixed(2)),
          escapeCsvValue(finding.summary || ''),
          escapeCsvValue(finding.recommendation || ''),
        ]);
        const csv = [header.join(','), ...rows.map((row) => row.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const fileName = `contract-findings-${new Date().toISOString().slice(0, 10)}.csv`;
        const { signedUrl } = await uploadExportArtifact(
          blob,
          fileName,
          'analysis-csv',
          ['findings-export', 'format:csv'],
          'Contract analyzer findings (CSV)'
        );
        openSignedDownload(signedUrl);
        toast.success('Findings exported to storage (CSV)');
        return;
      }

      const lines: string[] = [];
      lines.push(`# Contract Analyzer Findings`);
      lines.push(`- Date: ${new Date().toISOString()}`);
      lines.push(`- Contract Type: ${contractType}`);
      lines.push(`- Jurisdictions: ${selectedJurisdictions.join(', ')}`);
      lines.push('');
      filteredFindings.forEach((finding, idx) => {
        lines.push(`## ${idx + 1}. ${finding.title}`);
        lines.push(`- Severity: ${finding.severity}`);
        lines.push(`- Category: ${finding.category}`);
        lines.push(`- Jurisdiction: ${finding.jurisdiction}`);
        lines.push(`- Confidence: ${(finding.confidence?.score ?? 0).toFixed(2)}`);
        lines.push('');
        lines.push('**Summary**');
        lines.push(finding.summary);
        lines.push('');
        lines.push('**Clause excerpt**');
        lines.push('');
        lines.push('```text');
        lines.push(finding.clauseSpan?.text || '');
        lines.push('```');
        lines.push('');
        lines.push('**Recommendation**');
        lines.push(finding.recommendation);
        lines.push('');
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
      const fileName = `contract-findings-${new Date().toISOString().slice(0, 10)}.md`;
      const { signedUrl } = await uploadExportArtifact(
        blob,
        fileName,
        'analysis-markdown',
        ['findings-export', 'format:markdown'],
        'Contract analyzer findings (Markdown)'
      );
      openSignedDownload(signedUrl);
      toast.success('Findings exported to storage (Markdown)');
    } catch (err) {
      console.error('Export failed', err);
      toast.error(err instanceof Error ? err.message : 'Failed to export findings');
    } finally {
      setExportingType(null);
    }
  };

  const handleExportMarkdown = () => {
    void exportFindings('markdown');
  };

  const handleExportJSON = () => {
    void exportFindings('json');
  };

  const handleExportCSV = () => {
    void exportFindings('csv');
  };

  const handleSave = async () => {
    setError(null);
    if (!findings || findings.length === 0) {
      setError('No findings to save. Please analyze a contract first.');
      return;
    }
    // Detect contract type (non-blocking suggestion)
    let effectiveContractType: ContractType = contractType;
    try {
      const detection = detectContractType(file ? (await file.text()) : '');
      if (detection && detection.confidence >= 0.7 && detection.type !== contractType && detection.type !== 'Other') {
        const accept = window.confirm(
          `This looks like a ${detection.type} contract (confidence ${(detection.confidence * 100).toFixed(0)}%).\nSwitch to ${detection.type} rules and prompts?\nReasons: ${detection.reasons.join(', ')}`
        );
        if (accept) {
          effectiveContractType = detection.type as ContractType;
          setContractType(detection.type as ContractType);
        }
      }
    } catch (_) {
      // ignore detector errors
    }

    const config: AnalyzerConfig = {
      jurisdictions: selectedJurisdictions as Jurisdiction[],
      primaryJurisdiction: primaryJurisdiction as Jurisdiction,
      contractType: effectiveContractType,
      preferences: {
        nec3FloatOwnership,
        nec3CEFloatTreatment,
      }
    };
    try {
      setIsSaving(true);
      const result = await saveRecommendations({ config, findings });
      if ('id' in result) {
        toast.success('Recommendations saved');
      } else if ('message' in result) {
        toast.success(result.message || 'Saved');
      } else {
        toast.success('Saved');
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save recommendations';
      toast.error(message);
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleJurisdiction = (j: Jurisdiction) => {
    setSelectedJurisdictions((prev) =>
      prev.includes(j) ? prev.filter((x) => x !== j) : [...prev, j]
    );
    if (!selectedJurisdictions.includes(j)) {
      setPrimaryJurisdiction(j);
    }
  };

  const handleAnalyze = async () => {
    setError(null);
    setFindings([]);
    if (!file) {
      setError('Please upload a contract file first.');
      return;
    }
    const config: AnalyzerConfig = {
      jurisdictions: selectedJurisdictions as Jurisdiction[],
      primaryJurisdiction: primaryJurisdiction as Jurisdiction,
      contractType,
      preferences: {
        nec3FloatOwnership,
        nec3CEFloatTreatment,
      }
    };
    try {
      setIsAnalyzing(true);
      // Beta: only text files supported
      const extraction = await extractContractText(file, config);
      // Run local rule-based checks (MVP)
      const ruleFindings = runLocalRules(extraction.text, config);
      // LLM-based checks (chunked for long docs)
      let llmFindings: Finding[] = [];
      let tokensUsed = 0;
      try {
        const useChunked = extraction.text.length > 40000;
        if (useChunked) {
          const res = await analyzeWithLLMChunked(extraction.text, config);
          llmFindings = res.findings || [];
          tokensUsed = Number(res.meta?.tokenUsage?.input || 0);
        } else {
          const res = await analyzeWithLLM(extraction.text, config);
          llmFindings = res.findings || [];
          tokensUsed = Number(res.meta?.tokenUsage?.input || 0);
        }
      } catch (llmErr) {
        console.warn('LLM analysis failed, proceeding with rule-based only:', llmErr);
      }
      let merged = [...ruleFindings, ...llmFindings];
      setTokenUsage(tokensUsed);
      // If still empty, keep a small placeholder to confirm flow
      if (merged.length === 0) {
        const demoFinding: Finding = {
          id: 'demo-1',
          category: 'Other',
          severity: 'low',
          actionType: 'draft',
          title: 'Beta placeholder finding',
          summary: 'Extraction completed successfully. Analyzer engine returned no findings.',
          recommendation: 'Adjust prompts or upload a richer sample to test. More rules are coming.',
          clauseSpan: {
            text: extraction.text.slice(0, 160) + (extraction.text.length > 160 ? '…' : ''),
            pageStart: 1,
            pageEnd: 1,
            startIndex: 0,
            endIndex: Math.min(160, extraction.text.length)
          },
          jurisdiction: primaryJurisdiction,
          contractType,
          confidence: { score: 0.2, rationale: 'Demo placeholder' },
          createdAt: new Date().toISOString()
        };
        merged = [demoFinding];
      }
      setFindings(merged);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error during analysis';
      setError(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <section className="py-10 relative overflow-hidden">
      <div className="container relative z-10 mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-extrabold text-gray-900">Contract Analyzer</h1>
          <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800 font-semibold">Beta</span>
        </div>
        <p className="mb-6 text-gray-600 max-w-3xl">
          Analyze contracts for potential risks, missing protections, and beneficial loopholes. Runs in your browser by default; you can choose to save recommendations later. Not legal advice.
        </p>

        {/* Config Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type</label>
              <select
                className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                value={contractType}
                onChange={(e) => setContractType(e.target.value as ContractType)}
              >
                <option value="NDA">NDA</option>
                <option value="MSA">MSA</option>
                <option value="SaaS">SaaS</option>
                <option value="SOW">SOW</option>
                <option value="Employment">Employment</option>
                <option value="DPA">DPA</option>
                <option value="NEC3">NEC3</option>
                <option value="JCT">JCT</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload Document (PDF, DOCX, or TXT)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt,.rtf"
                onChange={onFileChange}
                className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {file && (
                <p className="mt-2 text-sm text-gray-500">Selected: {file.name}</p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Jurisdictions</label>
            <div className="flex flex-wrap gap-2">
              {jurisdictions.map((j) => (
                <button
                  key={j}
                  type="button"
                  onClick={() => toggleJurisdiction(j)}
                  className={`px-3 py-1 rounded-full text-sm border transition ${
                    selectedJurisdictions.includes(j)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {j}
                </button>
              ))}
            </div>
            {selectedJurisdictions.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Jurisdiction</label>
                <select
                  className="w-full md:w-80 rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={primaryJurisdiction}
                  onChange={(e) => setPrimaryJurisdiction(e.target.value as Jurisdiction)}
                >
                  {selectedJurisdictions.map((j) => (
                    <option key={j} value={j}>{j}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Contract-type specific preferences */}
          {(contractType === 'NEC3' || contractType === 'JCT') && (
            <div className="mt-6 border-t pt-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Analysis Preferences (optional)</h3>
              {contractType === 'NEC3' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Programme Float Ownership</label>
                    <select
                      className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={nec3FloatOwnership}
                      onChange={(e) => setNec3FloatOwnership(e.target.value as any)}
                    >
                      <option value="Neutral">Neutral / Not specified</option>
                      <option value="Contractor">Contractor</option>
                      <option value="Employer">Employer/Client</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CE Assessment Treatment of Float</label>
                    <select
                      className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={nec3CEFloatTreatment}
                      onChange={(e) => setNec3CEFloatTreatment(e.target.value as any)}
                    >
                      <option value="NotSpecified">Not specified</option>
                      <option value="IgnoreFloat">Ignore float before granting time</option>
                      <option value="UseFloat">Use available float first</option>
                    </select>
                  </div>
                </div>
              )}
              {contractType === 'JCT' && (
                <p className="text-sm text-gray-600">No special preferences yet. Standard JCT checks will run.</p>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleAnalyze}
            disabled={!file || isAnalyzing}
            className="inline-flex items-center px-5 py-2.5 rounded-md bg-blue-600 text-white font-semibold shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? 'Analyzing…' : 'Analyze Contract'}
          </button>
          <button
            onClick={handleSave}
            disabled={findings.length === 0 || isSaving}
            className="inline-flex items-center px-5 py-2.5 rounded-md bg-green-600 text-white font-semibold shadow-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving…' : 'Save Recommendations'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/legal-hr')}
            className="inline-flex items-center px-5 py-2.5 rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
          >
            Back to Legal & HR
          </button>
        </div>

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-800 p-4 rounded-md text-sm">
            {error}
          </div>
        )}

        {findings.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between">
              <FindingsFilters findings={findings} filters={filters} onChange={setFilters} />
              <button
                type="button"
                onClick={() => setFilters((f) => ({ ...f, amendmentsOnly: !f.amendmentsOnly }))}
                className={`ml-4 inline-flex items-center px-3 py-1 rounded-full border text-sm ${filters.amendmentsOnly ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'}`}
                title="Toggle to show only Schedule of Amendments-related findings"
              >
                {filters.amendmentsOnly ? 'Amendments: ON' : 'Amendments: OFF'}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleExportMarkdown}
                disabled={exportingType === 'markdown'}
                className="inline-flex items-center px-4 py-2 rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {exportingType === 'markdown' ? 'Exporting…' : 'Export Markdown'}
              </button>
              <button
                type="button"
                onClick={handleExportJSON}
                disabled={exportingType === 'json'}
                className="inline-flex items-center px-4 py-2 rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {exportingType === 'json' ? 'Exporting…' : 'Export JSON'}
              </button>
              <button
                type="button"
                onClick={handleExportCSV}
                disabled={exportingType === 'csv'}
                className="inline-flex items-center px-4 py-2 rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {exportingType === 'csv' ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>
            <FindingsView
              findings={filteredFindings}
              onChipClick={(type, value) => {
                if (type === 'severity') setFilters((f) => ({ ...f, severity: value as any }));
                if (type === 'category') setFilters((f) => ({ ...f, category: value as any }));
                if (type === 'jurisdiction') setFilters((f) => ({ ...f, jurisdiction: value as any }));
              }}
            />
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-8 bg-yellow-50 border border-yellow-100 p-4 rounded-lg text-sm text-yellow-800">
          This tool is in Beta and provides informational guidance only. It does not constitute legal advice. Consult a qualified legal professional for your specific circumstances.
        </div>
      </div>
    </section>
  );
};

export default ContractAnalyzer;
